import {
	DocumentType,
	SubmissionWithParticipantsAndValidDecennialCertificate,
} from "@dune/types";
import {
	Button,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { FileStatusChip } from "../../../../component/common/files/FileStatusChip";
import { AddDocumentButton } from "../../../../component/constructionSites/submission/AddDocumentButton";
import { useLinkFileToConstructionSiteReception } from "../../../../api/mutations/constructionSiteReception.mutations";

interface ParticipantDocumentsReceptionTableProps {
	submissionParticipant: SubmissionWithParticipantsAndValidDecennialCertificate["participantToConstructionSiteSubmissions"][number];
	constructionSiteId: number;
	onDocumentClick?: (file: {
		id: string;
		key: string;
		bucket: string;
		mimeType: string;
		name: string;
	}) => void;
	loadingDocumentId?: string | null;
}

const getDocumentLabel = (documentType: DocumentType): string => {
	const labels: Partial<Record<DocumentType, string>> = {
		ATTESTATION_DECENNALE: "Attestation Décennale",
		CONVENTION_HONORAIRES: "Convention d'honoraires",
		DEVIS: "Devis",
		FACTURE: "Facture finale",
		PV_RECEPTION: "PV de réception",
		RAPPORT_INITIAL_CONTROLE_TECHNIQUE: "Rapport",
		RAPPORT_FINAL_CONTROLE_TECHNIQUE: "Rapport",
		RAPPORT_ETUDE_TECHNIQUE: "Rapport",
		RAPPORT_GEOTECHNIQUE: "Rapport",
	};
	return labels[documentType] || documentType.replace(/_/g, " ").toLowerCase();
};

export const ParticipantDocumentsReceptionTable = ({
	submissionParticipant,
	constructionSiteId,
	onDocumentClick,
	loadingDocumentId,
}: ParticipantDocumentsReceptionTableProps) => {
	const participant = submissionParticipant.participant;
	const isNewParticipant =
		"isNewForReception" in participant ? participant.isNewForReception : true;

	const linkReceptionDoc =
		useLinkFileToConstructionSiteReception(constructionSiteId);

	const requiredDocuments: DocumentType[] = [];
	if (submissionParticipant.type === "DESIGNER") {
		requiredDocuments.push("FACTURE");
		if (submissionParticipant.designerType === "CONTROLEUR_TECHNIQUE") {
			requiredDocuments.push("RAPPORT_FINAL_CONTROLE_TECHNIQUE");
		}
		if (isNewParticipant) requiredDocuments.push("ATTESTATION_DECENNALE");
	} else if (submissionParticipant.type === "BUILDER") {
		requiredDocuments.push("FACTURE", "PV_RECEPTION");
		if (isNewParticipant) requiredDocuments.push("ATTESTATION_DECENNALE");
	} else if (submissionParticipant.type === "MATERIAL") {
		requiredDocuments.push("FACTURE");
	}

	const getReceptionFilesForType = (documentType: DocumentType) =>
		participant.filesOnConstructionSiteReception
			?.filter((f) => f.type === documentType)
			.map((f) => f.File) || [];

	const findFilesForType = (documentType: DocumentType) => {
		const files = [...getReceptionFilesForType(documentType)];
		if (documentType === "ATTESTATION_DECENNALE") {
			files.push(
				...(submissionParticipant?.decennialCertificatesOfSubmissionOrValidFromOthers?.map(
					(cert) => cert.file,
				) || []),
			);
		}
		return files;
	};

	return (
		<div className="space-y-4">
			<h3 className="text-lg font-medium">Pièces justificatives</h3>
			<div className="rounded-lg border border-gray-200">
				<Table className="table-fixed w-full">
					<TableHeader>
						<TableRow>
							<TableHead>Type</TableHead>
							<TableHead>Fichier(s)</TableHead>
							<TableHead>Statut</TableHead>
							<TableHead>Actions</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{requiredDocuments.map((documentType) => {
							const files = findFilesForType(documentType);
							return (
								<TableRow key={documentType}>
									<TableCell className="truncate">
										{getDocumentLabel(documentType)}
									</TableCell>
									<TableCell className="truncate align-top">
										<div className="flex flex-col gap-2">
											{files.length > 0 ? (
												files.map((file) => (
													<Button
														key={file.id}
														variant="link"
														size="sm"
														type="button"
														className="p-0 py-1 h-auto font-normal text-primary text-left justify-start"
														onClick={() => onDocumentClick?.(file)}
														disabled={loadingDocumentId === file.id}
													>
														{file.name}
														{loadingDocumentId === file.id &&
															" (Chargement...)"}
													</Button>
												))
											) : (
												<span className="text-sm text-gray-500">
													Aucun document
												</span>
											)}
										</div>
									</TableCell>
									<TableCell className="truncate align-top">
										<div className="flex flex-col gap-2">
											{files.map((file) => (
												<div key={file.id}>
													<FileStatusChip
														humanProcessingStatus={file.humanProcessingStatus}
													/>
												</div>
											))}
										</div>
									</TableCell>
									<TableCell className="truncate align-top">
										<AddDocumentButton
											constructionSiteId={constructionSiteId}
											onUploadSuccess={(fileId) => {
												linkReceptionDoc.mutate({
													fileId,
													type: documentType,
													participantId: participant.id,
												});
											}}
										/>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</div>
		</div>
	);
};
